import { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { Box, Grid, GridProps, Stack, Typography } from '@mui/material';

import { AllPropertiesDescription, useOfferingFilters } from '@arrived/contexts';

export const PropertyGridBanner = (props: Omit<GridProps, 'title'>) => {
  const { featuredFilters, featuredFilterIdx } = useOfferingFilters();

  const banner = featuredFilters[featuredFilterIdx ?? 0].banner;

  if (!banner) {
    return null;
  }

  const { title, description, children, image } = banner;

  return (
    <PropertyGridBannerContent
      title={<FormattedMessage {...title} />}
      description={description ? <FormattedMessage {...description} /> : <AllPropertiesDescription />}
      {...props}
    >
      {/* The `image` has `alt` as a required prop if there is an image */}
      {/* eslint-disable-next-line jsx-a11y/alt-text */}
      {children ?? (image && <img {...image} />)}
    </PropertyGridBannerContent>
  );
};

interface PropertyGridBannerContentProps extends Omit<GridProps, 'title'> {
  children?: ReactNode;
  title: ReactNode;
  description: ReactNode;
}

const PropertyGridBannerContent = ({ children, title, description, sx, ...rest }: PropertyGridBannerContentProps) => (
  <Grid
    container
    spacing={{ xs: 0, md: 1 }}
    wrap="nowrap"
    sx={{ backgroundColor: ({ palette }) => palette.neutrals.milk, ...sx }}
    data-e2e="property-grid-banner"
    overflow="hidden"
    {...rest}
  >
    <Grid
      item
      container
      direction="column"
      sx={{
        flex: {
          xs: '1 1 auto',
          md: '1 0.1 600px',
        },
      }}
    >
      <Stack
        textAlign={{ xs: 'center', md: 'left' }}
        height="100%"
        justifyContent="center"
        maxWidth={{ xs: 'none', md: '40vw' }}
        spacing={{ xs: 1, md: 2 }}
        ml={{ xs: 0, md: 10 }}
        py={{ xs: 4, md: 0 }}
      >
        <Typography
          sx={(theme) => ({
            fontSize: { xs: theme.typography.h6.fontSize, md: theme.typography.h2.fontSize },
            lineHeight: { xs: theme.typography.body1.lineHeight, md: theme.typography.h2.lineHeight },
            fontWeight: 'bold',
          })}
        >
          {title}
        </Typography>
        <Box width="100%" display="flex" justifyContent="center">
          <Typography
            sx={(theme) => ({
              maxWidth: { xs: '80%', md: 'unset ' },
              fontSize: { xs: theme.typography.body2.fontSize, md: theme.typography.h6.fontSize },
              lineHeight: { xs: theme.typography.body2.lineHeight, md: theme.typography.h6.lineHeight },
            })}
          >
            {description}
          </Typography>
        </Box>
      </Stack>
    </Grid>
    <Grid
      flex="0 2 0"
      item
      lineHeight={0}
      display={{ xs: 'none', md: 'flex' }}
      height="320px"
      sx={{ '& > *': { mt: 'auto' } }}
    >
      {children}
    </Grid>
  </Grid>
);
